import * as React from 'react';

import { useBoolean, useLockFn, useRequest } from 'ahooks';
import {
  Badge,
  Button,
  Divider,
  Form,
  Modal,
  notification,
  Select,
  Spin,
  Typography,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import {
  CheckOutlinedIcon,
  DeleteOutlinedIcon,
  ExclamationCircleOutlinedIcon,
  InboxOutlinedIcon,
} from 'components';
import { useUserValue } from 'context';
import { deleteUser, searchRoles } from 'services';
import { generateSelectOptions, PATHNAME } from 'utils';

const {
  LOCAL: {
    USERS: { BASE_URL: USERS_BASE_URL },
  },
  QUERY_PARAM: {
    DEFAULT: {
      PAGE_NUMBER: DEFAULT_PAGE_NUMBER,
      MAX_PAGE_SIZE: DEFAULT_MAX_PAGE_SIZE,
    },
  },
} = PATHNAME;

type RolesParams = Parameters<typeof searchRoles>[number];

type FieldType = Pick<ReturnType<typeof useUserValue>, 'roleName'>;

const UserFormAction = () => {
  const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();
  const [deleteLoading, { toggle: toggleDeleteLoading }] = useBoolean();
  const { id, defaultRoleName, mode } = useUserValue();
  const {
    data: rolesData,
    error: rolesError,
    params: rolesParams,
    loading: isRolesLoading,
    run: searchRolesRun,
  } = useRequest(
    async ({ searchKey }: Pick<RolesParams, 'searchKey'>) =>
      await searchRoles({
        searchKey,
        pageNumber: DEFAULT_PAGE_NUMBER,
        pageSize: DEFAULT_MAX_PAGE_SIZE,
      }),
    {
      defaultParams: [{ searchKey: '' }],
      debounceWait: 1000,
    },
  );
  const navigate = useNavigate();

  const handleDeleteUser = useLockFn(
    async ({ id }: Pick<Required<ReturnType<typeof useUserValue>>, 'id'>) => {
      toggleDeleteLoading();

      deleteUser({ id })
        .then(() => {
          navigate(USERS_BASE_URL);
          notification.success({
            message: 'کاربر با موفقیت حذف شد.',
          });
        })
        .catch(message => {
          notification.error({
            message,
          });
        })
        .finally(() => {
          toggleDeleteLoading();
        });
    },
  );

  const { total: totalRoles = 0, data: roles = [] } = rolesData ?? {};
  const { searchKey: currentSearchKey = '' } = rolesParams.at(0) ?? {};

  const rolesSelectOptions = generateSelectOptions(roles, 'id', 'name', 'id');

  return (
    <div>
      <div className='px-6 pt-4'>
        <Typography.Title level={3} className='mb-0 text-base font-medium'>
          انتشار کاربر
        </Typography.Title>
      </div>

      <Divider className='mb-8 mt-4' />

      <div className='px-6'>
        <Typography.Paragraph>
          <Badge
            text={`دسترسی فعلی: ${defaultRoleName || 'پیش‌فرض'}`}
            color='blue'
          />
        </Typography.Paragraph>
        <Form.Item<FieldType>
          label='دسترسی جدید'
          name='roleName'
          className='mb-2'
        >
          <Select
            allowClear
            showSearch
            filterOption={false}
            popupMatchSelectWidth={false}
            placeholder='دسترسی جدید کاربر را انتخاب کنید'
            options={rolesSelectOptions}
            notFoundContent={
              isRolesLoading ? (
                <div className='grid min-h-24 place-content-center gap-4'>
                  <Spin />
                  <Typography.Text className='text-[--ant-color-info]'>
                    در حال بارگذاری دسترسی‌ها...
                  </Typography.Text>
                </div>
              ) : rolesError ? (
                <div className='grid min-h-24 content-center justify-items-center gap-4'>
                  <ExclamationCircleOutlinedIcon className='text-2xl text-[--ant-color-error]' />
                  <Typography.Text type='danger'>
                    {rolesError instanceof Error
                      ? rolesError.message
                      : String(rolesError)}
                  </Typography.Text>
                </div>
              ) : !totalRoles ? (
                <div className='grid min-h-24 content-center justify-items-center gap-2.5'>
                  <InboxOutlinedIcon className='text-3xl' />
                  <Typography.Text type='secondary'>
                    <span>هیچ دسترسی‌ای با عنوان</span>
                    <span> &rdquo;{currentSearchKey}&rdquo; </span>
                    <span>وجود ندارد.</span>
                  </Typography.Text>
                </div>
              ) : undefined
            }
            onSearch={value => {
              const searchKey = value.trim().toLocaleLowerCase();
              searchRolesRun({ searchKey });
            }}
          />
        </Form.Item>
      </div>

      <div className='flex flex-wrap-reverse items-center gap-2 p-6 *:m-0 *:shrink *:grow *:basis-40'>
        {mode === 'Update' ? (
          <Form.Item>
            <Button
              block
              danger
              className='flex items-center'
              loading={deleteLoading}
              icon={<DeleteOutlinedIcon />}
              onClick={async () => {
                const confirmed = await deleteModalInstance.confirm({
                  title: 'آیا از حذف این کاربر اطمینان دارید؟',
                  width: 500,
                });
                if (id && confirmed) {
                  handleDeleteUser({ id });
                }
              }}
            >
              <span>حذف کاربر</span>
            </Button>
            {deleteModalContextHolder}
          </Form.Item>
        ) : null}

        <Form.Item>
          <Button
            block
            type='primary'
            htmlType='submit'
            icon={<CheckOutlinedIcon />}
          >
            {mode === 'Create' ? (
              <span>افزودن کاربر جدید</span>
            ) : (
              <span>ذخیره تغییرات</span>
            )}
          </Button>
        </Form.Item>
      </div>
    </div>
  );
};

export { UserFormAction };
